import {
  action, computed, flow, observable, reaction, makeObservable,
} from 'mobx';

import { zonedTimeToUtc } from 'date-fns-tz';
import { DEFAULT_TIME_ZONE_NAME } from '@shared/constants';
import BaseStore from './BaseStore';
import ExamSearchResultApi from '../api/exam_search_result';

class ExamSearchResultStore extends BaseStore {
  isSlotsDataLoading = false;
  slots = [];
  selectedDate;
  selectedTime;
  selectedCoordinates = {};
  timeZone;
  selectedExamCenter = {};
  selectedSessionId;
  selectedSiteId;

  constructor() {
    super();

    makeObservable(this, {
      isSlotsDataLoading: observable,
      slots: observable,
      selectedDate: observable,
      selectedTime: observable,
      selectedCoordinates: observable,
      timeZone: observable,
      selectedExamCenter: observable,
      selectedSessionId: observable,
      selectedSiteId: observable,
      combinedByTimestampSlots: computed,
      sortedSlots: computed,
      slotsForSelectedTime: computed,
      combinedByExamCentersSlots: computed,
      combinedByExamCentersSortedSlots: computed,
      firstThreeExamCenters: computed,
      setTimeZone: action,
      setSelectedDate: action,
      setSelectedTime: action,
      setSelectedCoordinates: action,
      setQueryParams: action,
      setSelectedExamCenter: action.bound,
      setSelectedSessionId: action.bound,
      setSelectedSiteId: action.bound,
    });

    reaction(
      () => this.selectedDate,
      (selectedDate) => this.getSlots({ latitude: this.selectedCoordinates.latitude, longitude: this.selectedCoordinates.longitude, date: selectedDate }),
    );
    reaction(
      () => this.selectedCoordinates,
      (selectedCoordinates) => {
        this.getSlots({
          latitude: selectedCoordinates.latitude,
          longitude: selectedCoordinates.longitude,
          date: this.selectedDate,
        });
      },
    );
  }

  get combinedByTimestampSlots() {
    return this.slots.reduce((result, slot) => {
      const currentResult = { ...result };
      const slotDateISO = new Date(slot.start_date).toISOString();
      if (currentResult.hasOwnProperty(slotDateISO)) {
        currentResult[slotDateISO].push(slot);
      } else {
        currentResult[slotDateISO] = [slot];
      }
      return currentResult;
    }, {});
  }

  get sortedSlots() {
    const slots = Object.keys(this.combinedByTimestampSlots);
    return slots.sort((date1, date2) => new Date(date1) - new Date(date2));
  }

  get slotsForSelectedTime() {
    if (this.selectedTime) {
      const searchDateISO = zonedTimeToUtc(`${this.selectedDate}T${this.selectedTime}:00`, this.timeZone ?? DEFAULT_TIME_ZONE_NAME).toISOString();
      if (this.combinedByTimestampSlots.hasOwnProperty(searchDateISO)) {
        return this.combinedByTimestampSlots[searchDateISO];
      }
      return [];
    }
    return [];
  }

  get combinedByExamCentersSlots() {
    return this.slots.reduce((result, slot) => {
      const currentResult = { ...result };
      const slotExamCenterId = slot.exam_center.id;
      if (currentResult.hasOwnProperty(slotExamCenterId)) {
        currentResult[slotExamCenterId].push(slot);
      } else {
        currentResult[slotExamCenterId] = [slot];
      }
      return currentResult;
    }, {});
  }

  get combinedByExamCentersSortedSlots() {
    const examCentersIds = Object.keys(this.combinedByExamCentersSlots);
    return examCentersIds.reduce((result, examCenterId) => {
      const clonedResult = { ...result };
      const clonedSlots = [...this.combinedByExamCentersSlots[examCenterId]];
      clonedResult[examCenterId] = clonedSlots.sort((slot1, slot2) => new Date(slot1.start_date) - new Date(slot2.start_date));
      return clonedResult;
    }, {});
  }

  get firstThreeExamCenters() {
    if (this.selectedTime) {
      return this.slotsForSelectedTime.slice(0, 3);
    }

    const firstThreeExamCenterIds = Object.keys(this.combinedByExamCentersSortedSlots).slice(0, 3);
    return firstThreeExamCenterIds.map((id) => this.combinedByExamCentersSortedSlots[id][0]);
  }

  setTimeZone(timeZone) {
    this.timeZone = timeZone;
  }

  setSelectedDate(date) {
    this.selectedDate = date;
  }

  setSelectedTime(time) {
    this.selectedTime = time;
  }

  setSelectedCoordinates(
    {
      latitude, longitude,
    },
  ) {
    this.selectedCoordinates = {
      latitude,
      longitude,
    };
  }

  setQueryParams(
    {
      date, time, latitude, longitude,
    },
  ) {
    this.setSelectedDate(date);
    this.setSelectedTime(time);
    this.setSelectedCoordinates({ latitude, longitude });
  }

  setSelectedExamCenter(center) {
    this.selectedExamCenter = center;
  }

  setSelectedSessionId(sessionId) {
    this.selectedSessionId = sessionId;
  }

  setSelectedSiteId(siteId) {
    this.selectedSiteId = siteId;
  }

  getSlots = flow(
    function* ({ latitude, longitude, date }) {
      this.isSlotsDataLoading = true;

      try {
        const { data } = yield ExamSearchResultApi.getSlots(latitude, longitude, date);
        this.slots = data;
        if (data.length) {
          this.setTimeZone(data[0].exam_center.timezone_name);
        }
      } finally {
        this.isSlotsDataLoading = false;
      }
    },
  );
}

export default new ExamSearchResultStore();
